import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ImageUploader from '../../Components/ImageUploader/ImageUploader';
import { getBrandsFromFirestore } from '../../firebase/brandService';
import { getGenresFromFirestore } from '../../firebase/genreService';
import { getColorsFromFirestore } from '../../firebase/colorService';
import { useAuth } from '../../firebase/AuthContext';
import styles from './Tools.module.css';
import SelectInput from '../../Components/SelectInput/SelectInput';
import { addToolToFirestore } from '../../firebase/toolService';
import { uploadToolImage } from '../../firebase/storageService';
import { Loading } from '../../Components';

import { getStatesFromFirestore } from '../../firebase/stateService';

const Tools = () => {
  const [toolName, setToolName] = useState('');
  const [loading, setLoading] = useState(false);
  const [brand, setBrand] = useState(''); // id
  const [genre, setGenre] = useState(''); // id
  const [color, setColor] = useState(''); // id
  const [state, setState] = useState(''); // id
  const [quantity, setQuantity] = useState(''); // string or number
  const [images, setImages] = useState([]); // フォームで選択中の画像
  const [description, setDescription] = useState(''); // 詳細
  const [toolList, setToolList] = useState([]); // [{ name, genre, brand, color, state, quantity, images: [] }]
  const [brandList, setBrandList] = useState([]);
  const [genreList, setGenreList] = useState([]);
  const [colorList, setColorList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [stockStateList, setStockStateList] = useState([]);
  const { user } = useAuth();
  // 画像追加用inputの一時ファイルstate（道具ごと）
  const [pendingImages, setPendingImages] = useState({}); // { [toolIdx]: File[] }
  const navigate = useNavigate();
  useEffect(() => {
    if (!user) return;
    (async () => {
      const brands = await getBrandsFromFirestore(user.uid);
      setBrandList(brands);
      const genres = await getGenresFromFirestore(user.uid);
      setGenreList(genres);
      const colors = await getColorsFromFirestore(user.uid);
      setColorList(colors);
      const states = await getStatesFromFirestore(user.uid, 'tool');
      setStateList(states);
      const stockStates = await getStatesFromFirestore(user.uid, 'stock');
      setStockStateList(stockStates);
    })();
  }, [user]);

  // 画像をbase64に変換するヘルパー
  const filesToBase64 = (files) => Promise.all(
    files.map(file => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    }))
  );

  const handleAddTool = async (e) => {
    e.preventDefault();
    if (!toolName.trim()) return;
    setLoading(true);
    let imageUrls = [];
    try {
      if (images.length > 0 && user) {
        imageUrls = await Promise.all(
          images.slice(0, 3).map((file, idx) => uploadToolImage(file, user.uid, toolName.trim(), idx))
        );
      }
      const toolObj = {
        name: toolName.trim(),
        genre: genre,
        brand: brand,
        color: color,
        state: state,
        quantity: quantity,
        description: description.trim(),
        images: imageUrls
      };
      await addToolToFirestore(toolObj);
      setToolName('');
      setGenre('');
      setBrand('');
      setColor('');
      setState('');
      setQuantity('');
      setDescription('');
      setImages([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
  // alert('道具を登録しました');
    } catch (err) {
      alert('登録に失敗しました: ' + err.message);
    } finally {
      setLoading(false);
    }
  };
  // 画像input用ref
  const fileInputRef = useRef();

  // フォーム画像選択
  const handleFormImageChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 3);
    setImages(files);
  };

  const handleImageChange = (toolIdx, e) => {
    const files = Array.from(e.target.files);
    setToolList(prevList => prevList.map((tool, idx) => {
      if (idx !== toolIdx) return tool;
      const merged = [...tool.images, ...files].slice(0, 3);
      return { ...tool, images: merged };
    }));
  };

  // 画像input変更時
  const handlePendingImageChange = (toolIdx, e) => {
    const files = Array.from(e.target.files).slice(0, 3 - (toolList[toolIdx]?.images?.length || 0));
    setPendingImages(prev => ({ ...prev, [toolIdx]: files }));
  };

  // 画像追加ボタンでtool.imagesに追加
  const handleAddPendingImages = (toolIdx) => {
    const files = pendingImages[toolIdx] || [];
    if (!files.length) return;
    setToolList(prevList => prevList.map((tool, idx) => {
      if (idx !== toolIdx) return tool;
      const merged = [...tool.images, ...files].slice(0, 3);
      return { ...tool, images: merged };
    }));
    setPendingImages(prev => ({ ...prev, [toolIdx]: [] }));
  };

  const handleRemoveImage = (toolIdx, imgIdx) => {
    setToolList(prevList => prevList.map((tool, idx) => {
      if (idx !== toolIdx) return tool;
      return { ...tool, images: tool.images.filter((_, i) => i !== imgIdx) };
    }));
  };

  const handleRemoveTool = (idx) => {
    setToolList(toolList.filter((_, i) => i !== idx));
  };

  if (loading) return <Loading />;

  return (
    <div className={styles.container}>
      <h2>道具保管ページ</h2>
      <button
        className={styles.button}
        style={{ marginBottom: 16 }}
        onClick={() => navigate('/tools/list')}
      >
        一覧に戻る
      </button>
      <form onSubmit={handleAddTool} className={styles.form} style={{ flexDirection: 'column', alignItems: 'stretch', gap: 0 }}>
        <label style={{ fontWeight: 'bold', marginBottom: 2 }}>道具名</label>
        <input
          type="text"
          value={toolName}
          onChange={(e) => setToolName(e.target.value)}
          placeholder="道具名を入力"
          className={styles.input}
        />
        <SelectInput
          label="ジャンル"
          value={genre}
          onChange={e => setGenre(e.target.value)}
          options={genreList}
          placeholder="ジャンルを選択"
          className={styles.input}
          style={{ marginTop: 4, marginBottom: 4 }}
          description="ジャンルは道具の種類（例：ニッパー、ヤスリ、ピンセットなど）を選択してください。"
        />
        <SelectInput
          label="ブランド"
          value={brand}
          onChange={e => setBrand(e.target.value)}
          options={brandList}
          placeholder="ブランドを選択"
          className={styles.input}
          style={{ marginTop: 0, marginBottom: 8 }}
          description="ブランドはメーカー名（例：タミヤ、ゴッドハンドなど）を選択してください。"
        />
        <SelectInput
          label="色"
          value={color}
          onChange={e => setColor(e.target.value)}
          options={colorList}
          placeholder="色を選択"
          className={styles.input}
          style={{ marginTop: 0, marginBottom: 8 }}
          description="色は道具の本体色や特徴的な色を選択してください。"
        />
        <SelectInput
          label="状態"
          value={state}
          onChange={e => setState(e.target.value)}
          options={stateList}
          placeholder="状態を選択"
          className={styles.input}
          style={{ marginTop: 0, marginBottom: 8 }}
          description="状態は道具の使用状態（例：未使用、使用中、壊れた等）を選択してください。"
        />
        <SelectInput
          label="残量"
          value={quantity}
          onChange={e => setQuantity(e.target.value)}
          options={[{ id: '', name: '残量を選択' }, ...stockStateList]}
          placeholder="残量を選択"
          className={styles.input}
          style={{ marginBottom: 8 }}
        />
        <label style={{ fontWeight: 'bold', marginBottom: 2 }}>画像</label>
        <ImageUploader images={images} setImages={setImages} max={3} label="画像" />
        <label style={{ fontWeight: 'bold', marginBottom: 2 }}>詳細</label>
        <textarea
          value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="道具の特徴やメモなど自由に記入"
          className={styles.input}
          style={{ minHeight: 60, marginBottom: 8, resize: 'vertical' }}
        />
        <button type="submit" className={styles.button}>追加</button>
      </form>
      <ul className={styles.toolList}>
        {toolList.map((tool, idx) => (
          <li key={idx} className={styles.toolItem}>
            <div>
              <div className={styles.toolName}>{tool.name}</div>
              {tool.genre && <div className={styles.toolBrand}>ジャンル: {genreList.find(g => g.id === tool.genre)?.name || tool.genre}</div>}
              {tool.brand && <div className={styles.toolBrand}>ブランド: {brandList.find(b => b.id === tool.brand)?.name || tool.brand}</div>}
              {tool.color && <div className={styles.toolBrand}>色: {colorList.find(c => c.id === tool.color)?.name || tool.color}</div>}
              <div className={styles.imageUploadBlock}>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={tool.images.length >= 3}
                  onChange={e => handlePendingImageChange(idx, e)}
                  style={{ marginRight: 8 }}
                />
                <button
                  type="button"
                  className={styles.addImageButton}
                  onClick={() => handleAddPendingImages(idx)}
                  disabled={tool.images.length >= 3 || !(pendingImages[idx]?.length > 0)}
                >
                  画像を追加
                </button>
                {pendingImages[idx]?.length > 0 && (
                  <span style={{ fontSize: '0.9em', color: '#888' }}>
                    {pendingImages[idx].length}枚選択中
                  </span>
                )}
              </div>
              {tool.images && tool.images.length > 0 && (
                <div className={styles.imagePreviewArea}>
                  {tool.images.map((img, i) => (
                    <div key={i} className={styles.imagePreviewWrapper}>
                      <img
                        src={URL.createObjectURL(img)}
                        alt={`道具画像${i+1}`}
                        className={styles.imagePreview}
                      />
                      <button
                        type="button"
                        className={styles.deleteImageButton}
                        onClick={() => handleRemoveImage(idx, i)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => handleRemoveTool(idx)} className={styles.deleteButton}>削除</button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Tools;
